import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../../css/signup.css";
import { Mail, Lock, User, Eye, EyeOff, Briefcase, FileText, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { registerUser } from "../../services/authService";
import { getCategories, getAllServiceProviders } from "../../services/categoriesService";
import Select from "react-select";
import ProfessionSelect from "../../components/auth/signup/ProfessionSelect";

const SignUp = () => {
  const [accountType, setAccountType] = useState("user"); // 'user', 'freelancer', 'business'
  const [showPassword, setShowPassword] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [errors, setErrors] = useState({});

  const { setAccessToken, setRefreshToken, setRole: setUserRole } = useAuth();
  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    business_name: "",
    bio: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // reset errors

    console.log(selectedCategory.id)


    // ✅ Profession validation for freelancer & business
    if ((accountType === "freelancer" || accountType === "business") && !selectedCategory?.id) {
      setErrors({ profession: "Please select a valid profession from the list." });
      return;
    }

    const data = {
      username: formData.username,
      first_name: formData.first_name,
      email: formData.email,
      password: formData.password,
      role: accountType,
      ...(accountType === "freelancer" && {
        first_name: formData.first_name,
        last_name: formData.last_name,
        bio: formData.bio,
        category_id: selectedCategory.id,
      }),
      ...(accountType === "business" && {
        business_name: formData.business_name,
        category_id: selectedCategory.id,
      }),
    };

    try {
      const response = await registerUser(data);
      const { access, username, role: userRole, expires_at } = response.data;

      // ✅ Only store access token and user info
      localStorage.setItem("access", access);
      localStorage.setItem("username", username);
      localStorage.setItem("expires_at", expires_at * 1000);
      localStorage.setItem("role", userRole);

      setAccessToken(access);
      setUserRole(userRole);

      // ✅ Navigate based on role
      switch (userRole) {
        case "freelancer":
          navigate("/home/freelancer-dashboard");
          break;
        case "business":
          navigate("/home/business-dashboard");
          break;
        default:
          navigate("/home");
      }
    } catch (err) {
      console.error("Signup failed", err);

      const data = err.response?.data;
      let fieldErrors = {};

      if (data?.username) fieldErrors.username = data.username;
      if (data?.email) fieldErrors.email = data.email;
      if (data?.error) fieldErrors.general = data.error;

      if (Object.keys(fieldErrors).length === 0) {
        fieldErrors.general = "Signup failed. Please check your information and try again.";
      }

      setErrors(fieldErrors);
    }
  };

  useEffect(() => {
    if (accountType === "freelancer" || accountType === "business") {
      getCategories(accountType)
        .then((data) => {

          const options = data.map((cat) => ({
            value: cat.id,
            label: cat.name,
          }));

          setCategories(options);
        })
        .catch(console.error);

      setSelectedCategory(null);
    }
  }, [accountType]);

  return (
    <div className="full-signup">
      <div className="signup-container">
        <form className="signup-form" onSubmit={handleSubmit}>
          <h2>
            {accountType === "freelancer"
              ? "Freelancer Sign Up"
              : accountType === "business"
                ? "Business Sign Up"
                : "Create Your Account"}
          </h2>

          {/* === Account Type Switch === */}
          <div className="signup-switch inner-switch">
            <button
              type="button"
              className={`switch-btn ${accountType === "user" ? "active" : ""}`}
              onClick={() => setAccountType("user")}
            >
              <i className="fa-solid fa-user"></i> User
            </button>

            <button
              type="button"
              className={`switch-btn ${accountType === "freelancer" ? "active" : ""}`}
              onClick={() => setAccountType("freelancer")}
            >
              <i className="fa-solid fa-user-tie"></i> Freelancer
            </button>

            <button
              type="button"
              className={`switch-btn ${accountType === "business" ? "active" : ""}`}
              onClick={() => setAccountType("business")}
            >
              <i className="fa-solid fa-building"></i> Business
            </button>
          </div>

          {accountType === "user" && (
            <div className="user-fields">
              {/* Email */}
              <div className="form-group with-icon">
                <div className="input-wrapper">
                  <Mail className="input-icon" size={18} />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                {errors.email && <div className="input-error">{errors.email}</div>}
              </div>

              {/* Display Name (optional) */}
              <div className="form-group with-icon">
                <User className="input-icon" size={18} />
                <input
                  type="text"
                  name="first_name"
                  placeholder="Display Name (optional)"
                  value={formData.first_name || ""}
                  onChange={handleChange}
                />
                {errors.first_name && <div className="input-error">{errors.first_name}</div>}
              </div>

              {/* Password */}
              <div className="form-group with-icon password-group">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </span>
              </div>
            </div>
          )}



          {/* === Freelancer Fields === */}
          {accountType === "freelancer" && (
            <div className="freelancer-fields">
              {/* Name Fields */}
              <div className="name-row">
                <div className="form-group with-icon">
                  <User className="input-icon" size={18} />
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group with-icon">
                  <User className="input-icon" size={18} />
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="form-group with-icon">
                <div className="input-wrapper">
                  <Mail className="input-icon" size={18} />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                {errors.email && <div className="input-error">{errors.email}</div>}
              </div>

              {/* Username (optional) */}
              <div className="form-group with-icon">
                <i className="fa-solid fa-at input-icon"></i>
                <input
                  type="text"
                  name="username"
                  placeholder="Username (optional)"
                  value={formData.username}
                  onChange={handleChange}
                />
                {errors.username && <div className="input-error">{errors.username}</div>}
              </div>

              {/* Password */}
              <div className="form-group with-icon password-group">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </span>
              </div>

              {/* Profession */}
              <div className="form-group">
                <ProfessionSelect
                  options={categories}
                  onSelect={(category) => setSelectedCategory(category)}
                />
              </div>
            </div>
          )}

          {/* === Business Fields === */}
          {accountType === "business" && (
            <div className="business-fields">
              {/* Business Name */}
              <div className="form-group with-icon">
                <Building2 className="input-icon" size={18} />
                <input
                  type="text"
                  name="business_name"
                  placeholder="Business name"
                  value={formData.business_name}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div className="form-group with-icon">
                <div className="input-wrapper">
                  <Mail className="input-icon" size={18} />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                {errors.email && <div className="input-error">{errors.email}</div>}
              </div>

              {/* Username (optional) */}
              <div className="form-group with-icon">
                <i className="fa-solid fa-at input-icon"></i>
                <input
                  type="text"
                  name="username"
                  placeholder="Username (optional)"
                  value={formData.username}
                  onChange={handleChange}
                />
                {errors.username && <div className="input-error">{errors.username}</div>}
              </div>

              {/* Password */}
              <div className="form-group with-icon password-group">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </span>
              </div>

              {/* Profession */}
              <div className="form-group">
                <ProfessionSelect
                  options={categories}
                  accountType={accountType}
                  onSelect={setSelectedCategory}
                />
              </div>

            </div>
          )}

          {/* === General Errors === */}
          {errors.general && <div className="form-error">{errors.general}</div>}

          {/* === Submit === */}
          <button className="signup-button" type="submit">
            {accountType === "freelancer"
              ? "Create Freelancer Account"
              : accountType === "business"
                ? "Create Business Account"
                : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignUp;